/* import __COLOCATED_TEMPLATE__ from './settings.hbs'; */
/* RESPONSIBLE TEAM: team-ml */

import Component from '@glimmer/component';
import type FinCustom from 'embercom/models/operator/visual-builder/step/fin-custom';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type FinCustomConfiguration from 'embercom/objects/visual-builder/configuration/step/fin-custom';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import {
  allowedAttributesForTestConfigPanel,
  escalationDestinationsForTestConfigPanel,
  type OutputParam,
  type AttributeCollection,
} from 'embercom/components/workflows/graph-editor/node-items/steps/fin-custom';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

interface Arguments {
  step: FinCustom;
  stepConfig: FinCustomConfiguration;
  editorState: EditorState;
  isReadOnly: boolean;
  onAddOutputParam: (outputParam: OutputParam) => AttributeDescriptor;
  onUpdateOutputParam: (outputParam: OutputParam) => void;
  onRemoveOutputParam: (outputParam: OutputParam) => void;
  outputParams: OutputParam[];
  isDraft: boolean;
}

export default class Settings extends Component<Signature> {
  @service appService: any;
  @service attributeService: any;
  @service store: any;
  @service contentEditorService: any;
  @service modalService: any;

  @tracked openInserters: any;
  @tracked selectedTab = 'configure_section';
  @tracked allowMultiTurn = this.args.step.allowMultiTurn;
  @tracked allowContentSearch = this.args.step.allowContentSearch;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);

    // Initialize allowMultiTurn if not already set
    if (this.args.step.allowMultiTurn === undefined) {
      this.args.step.set('allowMultiTurn', true);
      this.allowMultiTurn = true;
    }
  }

  get app() {
    return this.appService.app;
  }

  get allowedAttributes(): AttributeCollection {
    return allowedAttributesForTestConfigPanel(this.attributeService, this.args.step);
  }

  get canShowContentSearchToggle() {
    return this.app.canUseFeature('answerbot-fin-agentic-content-search');
  }

  get shouldShowAgenticSettings() {
    return this.canShowContentSearchToggle;
  }

  get shouldShowTestConfigPanel() {
    return (
      this.args.editorState.showTestConfigPanel &&
      this.args.step.isV3 &&
      (this.app.canUseFeature('procedures-closed-beta') ||
        this.app.canUseFeature('tasks-sophisticated-testing'))
    );
  }

  get showSdkView() {
    // if the feature is not enabled, we never show the preview view
    if (!this.app.canUseFeature('team-actions-sdk-preview')) {
      return false;
    }

    // we only show the preview view if there is procedure content
    if (!this.args.step.finProcedureContent?.content?.length) {
      return false;
    }

    let content = this.args.step.finProcedureContent.content;

    // use the preview view if there is any non-taskSection sections
    if (content.some((section) => section.type !== 'taskStep')) {
      return true;
    }

    // use the preview view if there is any non instructionTaskStep steps
    if (content.some((section) => section?.content?.type !== 'instructionTaskStep')) {
      return true;
    }

    // At this point we have a procedure, but it is fully compatible with instructions.
    // We dont need to use the preview view in this case.
    return false;
  }

  @action
  onSelectionChange(sectionId: string) {
    this.selectedTab = sectionId;
  }

  @action
  toggleAllowMultiTurn() {
    this.allowMultiTurn = !this.allowMultiTurn;
    this.args.step.set('allowMultiTurn', this.allowMultiTurn);
  }

  @action
  toggleAllowContentSearch() {
    this.allowContentSearch = !this.allowContentSearch;
    this.args.step.set('allowContentSearch', this.allowContentSearch);
  }

  get escalationDestinations() {
    return escalationDestinationsForTestConfigPanel(this.args.stepConfig, this.args.step);
  }

  @action
  async maybeSaveRuleset() {
    await this.contentEditorService.saveRuleset.perform({ skipConfirmation: true });
  }

  @action
  rulesetHasUnsavedChanges() {
    return this.contentEditorService.activeRulesetLink.hasUnsavedChanges;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinCustom::Settings': typeof Settings;
    'workflows/graph-editor/node-items/steps/fin-custom/settings': typeof Settings;
  }
}
